import { useState } from 'react';
import styles from 'styles/modules/MediaCoverage.module.scss';
import TitleUnderline from 'components/TitleUnderline';
import EmptyBlock from 'components/EmptyBlock';
import { useDispatch, useSelector } from 'react-redux';
import { editCoverage, removeCoverage } from 'store/company/coverage';
import {
    handleHidePopup,
    handleShowPopup,
    handleShowSidescreen,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import { authSelector } from 'store/auth';
import AddContent from './AddContent';
import MissedCoverage from './MissedCoverage';
import Card from 'components/Card';
import Link from 'next/link';
import Button from 'components/Button';
import Typo from 'components/Typo';
import { profile } from 'lang/en.json';

// This slice is server-rendered and indexed, so it must come out of the first
// render, not an effect. 45 is what clears 10k chars of text on a terse profile.
const resultsPerPage = 45;
// long enough that a real headline survives intact
const titleLimit = 120;

export default function Coverages({ data, total, companySlug }) {
    const { isAuthorized, isAdmin, company } = useSelector(authSelector);
    const dispatch = useDispatch();

    const [shown, setShown] = useState(resultsPerPage);
    const coverages = data || [];
    const resultsToShow = coverages.slice(0, shown);
    const loadMoreActive = shown < coverages.length;
    // `total` is the API's full count; `coverages` is only the page we fetched
    const coverageTotal = total || coverages.length;

    const handleRemovePopup = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: () => handleRemoveConfirm(id),
                onCancel: handleRemoveCancel,
                title: 'Delete Media coverage?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handleRemoveConfirm = async (id) => {
        dispatch(setPopupLoading(true));
        await dispatch(removeCoverage(id));
        dispatch(setPopupLoading(false));
        dispatch(handleHidePopup());
    };

    const handleRemoveCancel = () => {
        dispatch(handleHidePopup());
    };

    const handleAddCoverage = () => {
        dispatch(handleShowSidescreen('add-mediaCoverage'));
    };

    const handleEditCoverage = (id) => {
        dispatch(editCoverage(coverages.find((item) => item.id === id)));
        dispatch(handleShowSidescreen('edit-mediaCoverage'));
    };

    const handleLoadMore = () => setShown(shown + resultsPerPage);

    const isOnOwnedProfile = companySlug === company;
    const isSomeCoverageNew =
        isOnOwnedProfile && coverages.some(({ status }) => status === 'new');

    return (
        <div>
            <TitleUnderline className={'mb-m'}>
                <Typo Tag={'h2'} variant={'eyebrow'}>
                    <span data-nosnippet>{profile.media_coverage.title}</span>
                </Typo>

                {isAuthorized && (
                    <div className={styles.addBtnWrapper}>
                        {isAdmin ? (
                            <AddContent
                                data={{
                                    direction: 'row',
                                    label: `Add ${profile.media_coverage.title}`,
                                    onClick: handleAddCoverage,
                                    reverted: true,
                                    blue: true,
                                    labelHideMob: true,
                                }}
                            />
                        ) : (
                            <MissedCoverage className={styles.tellUs} />
                        )}
                    </div>
                )}
            </TitleUnderline>
            {isSomeCoverageNew && (
                <div className={styles.newCoverageNotification}>
                    <span>
                        More media coverage? Get you clever clogs. If you love
                        this latest piece of exposure, you can pin it to the top
                        of your Newspage so the whole world can see.
                    </span>
                    <span>
                        As we charge nothing, or very little for the work we do,
                        if {"you'd"} like to buy the editorial team a couple of
                        pints and a packet of Quavers, {"we'd"} be extremely
                        grateful. If you are struggling, however, as many small
                        businesses and charities are, please DO NOT. {"We've"}{' '}
                        got your back.
                    </span>
                    <a
                        href={
                            'https://pay.newspage.media/b/6oE2bP5sif2qaNW5kp?s=pli'
                        }
                        target={'_blank'}
                        rel="noreferrer"
                    >
                        Send a tip here
                    </a>
                </div>
            )}

            <div className="mb-l">
                <div className={styles.mediaCoverage}>
                    {resultsToShow.map(
                        (
                            { id, publisher, title, createdAt, url, status },
                            key
                        ) => {
                            return (
                                <Card
                                    pretitle={publisher}
                                    title={title}
                                    publishedAt={createdAt}
                                    key={`MediaCoverage-item-${key}`}
                                    link={url}
                                    onRemove={isAuthorized && handleRemovePopup}
                                    onEdit={isAdmin && handleEditCoverage}
                                    editLabel={
                                        profile.media_coverage.edit.button
                                    }
                                    cta={'Read more'}
                                    id={id}
                                    titleLimit={titleLimit}
                                    status={status}
                                />
                            );
                        }
                    )}
                </div>

                {loadMoreActive && (
                    <Button title="Load more" onClick={handleLoadMore} />
                )}

                {coverageTotal > resultsPerPage && (
                    <Link href={`/${companySlug}/media-coverage`}>
                        <a className={styles.viewAll}>
                            View all {coverageTotal} pieces of media coverage
                        </a>
                    </Link>
                )}

                {coverages.length < 1 && (
                    <EmptyBlock
                        title={profile.media_coverage.placeholder_empty}
                    />
                )}
            </div>
        </div>
    );
}
